import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { StyledTableRow } from "../../../../../styles/Shipmentstyles";
import { SortType } from "../../../../../models/SortType";
import {
  INVENTORY_VARIANT_SORT_BY,
  InventoryVariant,
} from "../../../../../models/inventory/InventoryAdmin";

interface InventoryDetailsItemsTableProps {
  rows: InventoryVariant[];
  isLoading?: boolean;
  page_no: number;
  limit: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  onProductClick?: (productId: number) => void;
  selectedSkus?: string[];
  canSelectRows?: boolean;
  onToggleSelect?: (row: InventoryVariant) => void;
  onToggleSelectAll?: () => void;
  onAdjustRow: (row: InventoryVariant) => void;
  onEditMetaRow: (row: InventoryVariant) => void;
  onAuditRow: (row: InventoryVariant) => void;
  canDeleteSku?: boolean;
  onDeleteRow?: (row: InventoryVariant) => void;
}

const getVariantDateValue = (row: InventoryVariant) => {
  const raw = row.createdAt || row.modifiedAt || "";
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
};

const compareVariantRows = (
  a: InventoryVariant,
  b: InventoryVariant,
  column: string,
) => {
  if (column === INVENTORY_VARIANT_SORT_BY.PRODUCT_NAME) {
    return (a.productName || "").localeCompare(b.productName || "", undefined, {
      sensitivity: "base",
    });
  }
  if (column === INVENTORY_VARIANT_SORT_BY.QUANTITY) {
    return (a.onHand ?? 0) - (b.onHand ?? 0);
  }
  if (column === INVENTORY_VARIANT_SORT_BY.DATE) {
    return getVariantDateValue(a) - getVariantDateValue(b);
  }
  return 0;
};

const InventoryDetailsItemsTable: React.FC<InventoryDetailsItemsTableProps> = ({
  rows,
  isLoading = false,
  page_no,
  limit,
  total_count,
  sortBy = "",
  sortType = SortType.DESC,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onProductClick,
  selectedSkus = [],
  canSelectRows = false,
  onToggleSelect,
  onToggleSelectAll,
  onAdjustRow,
  onEditMetaRow,
  onAuditRow,
  canDeleteSku = false,
  onDeleteRow,
}) => {
  const theme = useTheme();
  const [rowActionAnchor, setRowActionAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [activeRow, setActiveRow] = useState<InventoryVariant | null>(null);

  const allSelected =
    canSelectRows &&
    rows.length > 0 &&
    rows.every((row) => selectedSkus.includes(row.clientSku));
  const someSelected =
    canSelectRows &&
    !allSelected &&
    rows.some((row) => selectedSkus.includes(row.clientSku));
  const columnCount = canSelectRows ? 12 : 11;

  const sortedRows = useMemo(() => {
    try {
      if (!sortBy) return rows;
      const direction = sortType === SortType.ASC ? 1 : -1;
      return [...rows].sort(
        (a, b) => compareVariantRows(a, b, sortBy) * direction,
      );
    } catch {
      return rows;
    }
  }, [rows, sortBy, sortType]);

  const headerSortProps = onSortChange
    ? {
        sortBy,
        sortDirection: sortType,
        onSortChange,
      }
    : {};

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleOpenActions = (
    event: React.MouseEvent<HTMLElement>,
    row: InventoryVariant,
  ) => {
    setRowActionAnchor(event.currentTarget);
    setActiveRow(row);
  };

  const handleCloseActions = () => {
    setRowActionAnchor(null);
    setActiveRow(null);
  };

  const canSoftDeleteRow = (row: InventoryVariant | null) => {
    if (!canDeleteSku || !row) return false;
    if (row.active !== 1) return false;
    return (row.onHand ?? 0) === 0 && (row.reserved ?? 0) === 0;
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: window.innerHeight - 360 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor:
                  theme.palette.customColors?.tableGrayHeadingColor,
              }}
            >
              {canSelectRows && (
                <AppTableHeaderCell minWidth="50px" fontSize="12px">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={onToggleSelectAll}
                    disabled={rows.length === 0}
                    size="small"
                  />
                </AppTableHeaderCell>
              )}
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                S.NO
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sortColumn={INVENTORY_VARIANT_SORT_BY.PRODUCT_NAME}
                {...headerSortProps}
              >
                PRODUCT
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                ITEM
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
                sortColumn={INVENTORY_VARIANT_SORT_BY.QUANTITY}
                {...headerSortProps}
              >
                AVAILABLE
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                RESERVED
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="50px"
                fontSize="12px"
                sx={{ textAlign: "right" }}
              >
                PACK SIZE
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                BOX SIZE
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                TAG
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                STATUS
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="140px"
                fontSize="12px"
                sortColumn={INVENTORY_VARIANT_SORT_BY.DATE}
                {...headerSortProps}
              >
                DATE
              </AppTableHeaderCell>
              <AppTableHeaderCell minWidth="50px" fontSize="12px">
                ACTION
              </AppTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && rows.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={columnCount}>
                  <Box py={3} textAlign="center">
                    <CustomTypography variant="body2" color="text.secondary">
                      No items found for this inventory.
                    </CustomTypography>
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}
            {sortedRows.map((row, index) => (
              <StyledTableRow key={row.id || row.clientSku}>
                {canSelectRows && (
                  <AppTableRowCell minWidth="50px">
                    <Checkbox
                      checked={selectedSkus.includes(row.clientSku)}
                      onChange={() => onToggleSelect?.(row)}
                      size="small"
                    />
                  </AppTableRowCell>
                )}
                <AppTableRowCell minWidth="50px">
                  <CustomTypography variant="body2">
                    {(page_no - 1) * limit + index + 1}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell
                  textAlign="left"
                  align="left"
                  minWidth="50px"
                  onClick={() => {
                    if (row.productId != null && onProductClick) {
                      onProductClick(row.productId);
                    }
                  }}
                  sx={{
                    cursor:
                      row.productId != null && onProductClick
                        ? "pointer"
                        : "default",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.25}
                    minWidth={0}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      color={theme.palette.customColors?.blackText}
                    >
                      {row.productName || "—"}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      Product ID: {row.productId ?? "—"}
                    </CustomTypography>
                  </Box>
                </AppTableRowCell>
                <AppTableRowCell textAlign="left" align="left" minWidth="50px">
                  <SkuCopyRow sku={row.clientSku} fontWeight={600} />
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    textAlign="right"
                    width="100%"
                  >
                    <CustomTypography variant="body1" fontWeight={600}>
                      {row.free ?? 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      / {row.onHand ?? 0}
                    </CustomTypography>
                  </Box>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography variant="body2">
                    {row.reserved ?? 0}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                  <CustomTypography variant="body2">
                    {row.packSize ?? "—"}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <CustomTypography variant="body2">
                    {row.boxSize || "—"}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <CustomTypography variant="body2">
                    {row.tag || row.tagId || "—"}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <Chip
                    size="small"
                    label={row.active === 1 ? "Active" : "Inactive"}
                    sx={{
                      backgroundColor:
                        row.active === 1
                          ? theme.palette.customColors?.greenLight
                          : theme.palette.customColors?.grayBackGroundColor,
                      color: theme.palette.customColors?.text,
                      fontWeight: 600,
                    }}
                  />
                </AppTableRowCell>
                <AppTableRowCell minWidth="140px">
                  <CustomTypography variant="body2">
                    {row.createdAt || row.modifiedAt
                      ? formatDate(row.createdAt || row.modifiedAt)
                      : "—"}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell minWidth="50px">
                  <Button
                    variant="outlined"
                    onClick={(event) => handleOpenActions(event, row)}
                    sx={{
                      minWidth: "94px",
                      height: "32px",
                      fontSize: "0.8rem",
                      textTransform: "none",
                      borderColor: theme.palette.customColors?.greyBorder,
                      color: theme.palette.customColors?.text,
                    }}
                  >
                    Actions
                  </Button>
                </AppTableRowCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={Math.max(page_no - 1, 0)}
        onPageChange={(event, nextPage) => onPageChange(event, nextPage + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
      <Menu
        anchorEl={rowActionAnchor}
        open={Boolean(rowActionAnchor)}
        onClose={handleCloseActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            if (activeRow) onAdjustRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Adjust
          </CustomTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeRow) onEditMetaRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Edit Meta
          </CustomTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeRow) onAuditRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Audit
          </CustomTypography>
        </MenuItem>
        {canDeleteSku && (
          <MenuItem
            disabled={!canSoftDeleteRow(activeRow)}
            onClick={() => {
              if (activeRow && canSoftDeleteRow(activeRow)) {
                onDeleteRow?.(activeRow);
              }
              handleCloseActions();
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Delete SKU
            </CustomTypography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default observer(InventoryDetailsItemsTable);
